import {
  Check,
  Copy,
  Download,
  ExternalLink,
  FileOutput,
  FileText,
  Globe,
  Layers,
  Loader2,
  Lock,
  Maximize2,
  Minimize2,
  MoreVertical,
  PanelRightClose,
  PanelRightOpen,
  Palette,
  PenLine,
  Printer,
  Search,
  Sparkles,
} from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";

import { ErrorState } from "@/components/feedback";
import { CanvasView } from "@/components/reader/CanvasView";
import { DraggablePanel } from "@/components/reader/DraggablePanel";
import { ModeSwitch } from "@/components/reader/ModeSwitch";
import { PaperControl } from "@/components/reader/PaperControl";
import { PdfViewer, type PdfSelection } from "@/components/reader/PdfViewer";
import { Markdown } from "@/components/shared/Markdown";
import { TieredExportDialog } from "@/components/shell/TieredExportDialog";
import { Button } from "@/components/ui/button";
import { Input, ScrollArea, Skeleton } from "@/components/ui/primitives";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  Tip,
} from "@/components/ui/overlays";
import { DocTitleBar } from "@/components/workspace/content/DocTitleBar";
import {
  ExcalidrawSurface,
  loadExcalidraw,
  type DrawingSurfaceHandle,
} from "@/components/reader/drawing/ExcalidrawSurface";
import { loadFabric, type PersistHandlers } from "@/components/reader/fabric-editor";
import { InkModel, type MarkupBatchRequest, type MarkupBatchResult } from "@/components/reader/ink-model";
import {
  keys,
  useDocument,
  useDrawing,
  useDrawingConfig,
  useMarkup,
  useMarkupConfig,
  useReader,
  useSetDocumentVisibility,
} from "@/hooks/queries";
import { useAiStream } from "@/hooks/use-ai-stream";
import { useIsCompact } from "@/hooks/use-media-query";
import { trackJob } from "@/hooks/use-upload";
import { api, GraphMindApiError, queryClient, type Annotation } from "@/lib/api";
import { downloadArtifact, downloadUrlAsFile, extensionFor, sanitizeFilename } from "@/lib/download";
import { notifyError } from "@/lib/errors";
import { cn, timeAgo } from "@/lib/utils";
import { useActivity } from "@/stores/activity";
import { useAnnotationTool } from "@/stores/annotation-tool";
import { useShell } from "@/stores/shell";
import { useTabs } from "@/stores/tabs";
import { resolveEngine, useWritingEngine } from "@/stores/writing-engine";

type Selection = { text: string; page?: number };
type Mode = "chunk" | "with_context" | "personalized";

const MODES: { mode: Mode; label: string; hint: string }[] = [
  { mode: "chunk", label: "Explain", hint: "Explain just this selection — saved to Annotations" },
  { mode: "with_context", label: "In context", hint: "Explain it within the whole document" },
  { mode: "personalized", label: "For me", hint: "Explain it for how you've been learning" },
];

/** A failure worth retrying: a network/abort error (no status) or a transient
 * server condition. The cold-auth 401 belongs here — the first markup writes can
 * race ahead of the id-token cookie, and a beat later the same request succeeds.
 * Other 4xx (400/404/422) are the client's fault and won't improve on retry. */
function isTransient(e: unknown): boolean {
  if (e instanceof GraphMindApiError) {
    return e.status === 401 || e.status === 408 || e.status === 429 || e.status >= 500;
  }
  return true;
}

/** What to say when the paint bucket declines. Each case is a real thing the
 * user did, not an error: they clicked the line instead of the space, the area
 * they clicked runs off the page, or it is too small to have been meant. */
const FILL_REFUSALS: Record<string, string> = {
  "on-stroke": "Tap inside an area, not on the ink.",
  unbounded: "That area isn't closed — the fill would run off the page. Close the outline and try again.",
  "too-small": "That area is too small to fill.",
  "no-shape": "Nothing to fill there.",
};

/** Why the server refused a markup write, in one line the user (or whoever they
 * forward it to) can act on.
 *
 * The generic "the server rejected it" hid a whole class of problem: a studio
 * talking to an API that PREDATES an annotation type rejects every one of them,
 * and the toast looked identical to a client bug. FastAPI's 422 body names the
 * offending field and value, so the version skew announces itself — the shape
 * tools shipped against a backend that had been running since before they
 * existed, and the answer was "restart the API", not "debug the editor". */
export function rejectionReason(error: unknown): string {
  const generic = "The server rejected it, so it won't sync. Please try that edit again.";
  if (!(error instanceof GraphMindApiError)) return generic;
  let detail: unknown = error.detail;
  try {
    detail = JSON.parse(error.detail);
  } catch {
    /* a plain-text body is fine — used as-is below */
  }
  // FastAPI validation errors are a list of {loc, msg, input}. The first one is
  // the actionable one; a rejected `type` means the API doesn't know this tool.
  const first = Array.isArray((detail as { detail?: unknown })?.detail)
    ? (detail as { detail: unknown[] }).detail[0]
    : Array.isArray(detail)
      ? detail[0]
      : null;
  const msg = (first as { msg?: string } | null)?.msg;
  if (msg) {
    const field = ((first as { loc?: unknown[] }).loc ?? []).slice(-1)[0];
    const where = typeof field === "string" ? `${field}: ` : "";
    return `${error.status} — ${where}${msg}. If this is a tool the API doesn't know yet, its version is older than the studio's.`;
  }
  const text = typeof detail === "string" ? detail : error.detail;
  return text ? `${error.status} — ${text.slice(0, 180)}` : generic;
}

/** Run a batch write with a few backoff retries. A transient blip (network, cold
 * auth) shouldn't park the whole batch on the first try — this rides over it. A
 * failure that survives all retries throws, and the InkModel parks + replays it. */
async function withRetry<T>(op: () => Promise<T>, attempts = 3): Promise<T> {
  for (let i = 0; ; i++) {
    try {
      return await op();
    } catch (e) {
      if (i >= attempts - 1 || !isTransient(e)) throw e;
      await new Promise((r) => setTimeout(r, 200 * 2 ** i)); // 200ms, 400ms
    }
  }
}

/**
 * The reader: the document surface only — its own competing header/toolbar
 * (an "assistant" toggle + embedded AI/annotate sidebar) was stripped as part
 * of the docview rearchitecture. Chat now lives exclusively in the single
 * ChatLauncher/ask panel; saved AI explanations + notes surface in the
 * AppShell RightBar's Annotations pane. The one reader-local affordance left
 * is the handwriting transcript toggle, plus a small "explain this selection"
 * action bar that fires straight to /api/ai/explain (save: true) rather than
 * opening its own chat surface. Annotation TOOLS (pen/highlighter/etc, in
 * PdfViewer/CanvasView/AnnotationToolbar) are untouched.
 */
export function ReaderTab({
  docId,
  visible,
  panelId,
}: {
  docId: string;
  visible: boolean;
  /** The OWNING workspace panel's id, threaded from PanelContent. The 404
   * self-close below closes exactly this panel; absent (compact/legacy mounts)
   * ⇒ fall back to the computed `doc:${docId}` id. */
  panelId?: string;
}) {
  const reader = useReader(docId);
  const docQuery = useDocument(docId, { poll: reader.data?.document.status === "processing" });
  const doc = docQuery.data ?? reader.data?.document;

  // A document deleted since this tab was opened (an explicit delete, or the
  // integrity scanner's orphan cleanup — see api/integrity) 404s here forever.
  // The workspace layout (`graphmind.workspace.layout.v1`) persists tabs across
  // reloads/sessions, so without this the tab becomes a permanent ghost:
  // reopening the app (or hitting "reload" on a generic error state) just
  // re-mounts the same stale tab and 404s again. A confirmed 404 is the one
  // error worth self-healing — close the tab outright rather than leave "This
  // document could not be loaded" stuck in the strip.
  useEffect(() => {
    if (reader.error instanceof GraphMindApiError && reader.error.status === 404) {
      toast.error("That document was deleted", { description: "Closing this tab." });
      useTabs.getState().close(panelId ?? `doc:${docId}`);
    }
  }, [reader.error, docId, panelId]);

  const [selection, setSelection] = useState<Selection | null>(null);
  // A lasso "scan" of a circled page region, awaiting an AI figure-explain.
  const [regionScan, setRegionScan] = useState<{ image: string; page: number } | null>(null);
  const [printing, setPrinting] = useState(false);
  // The download control is a per-type menu when 2+ artifacts exist (see
  // `downloadItems` below). `busyDownload` is the KEY of the item currently
  // rendering (drives its in-item spinner + the trigger spinner); `null` = idle.
  // `downloadMenuOpen` keeps the menu open while an item renders so its spinner
  // stays visible (Base UI closes on select by default — see `runDownload`).
  const [busyDownload, setBusyDownload] = useState<string | null>(null);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const downloading = busyDownload !== null;
  const [tieredOpen, setTieredOpen] = useState(false);
  // The paper panel (⋯ → Paper) — canvas notes only; a PDF has its own paper.
  const [paperOpen, setPaperOpen] = useState(false);
  const setVisibility = useSetDocumentVisibility(docId);
  const isPublic = doc?.visibility === "public";
  const compact = useIsCompact();
  // Rename the document from the thin title bar (and, for a canvas, this is the
  // note's only name). PATCH /api/documents/{id} { title } renames the vault
  // file; invalidating the doc queries syncs the Dockview tab title too (via the
  // panel title-sync in PanelContent). One rename path for every doc type —
  // canvas / pdf / web / text (their bodies aren't editable, so there's no H1 to
  // reconcile; only the thin bar names them — redesign point 4).
  const renameDoc = useMutation({
    mutationFn: (next: string) => api.request("PATCH", `/api/documents/${docId}`, { title: next }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.document(docId) });
      void queryClient.invalidateQueries({ queryKey: keys.reader(docId) });
      void queryClient.invalidateQueries({ queryKey: keys.documents });
      void queryClient.invalidateQueries({ queryKey: keys.vault });
      void queryClient.invalidateQueries({ queryKey: keys.overview });
    },
    onError: (e) => toast.error("Could not rename", { description: String(e) }),
  });
  // The handwriting transcript is the one remaining reader-local side panel
  // (annotate/chat both moved out — see the doc comment above). The manual
  // toggle wins; default closed so the document gets the full width.
  const [transcriptManual, setTranscriptManual] = useState<boolean | null>(null);
  const transcriptOpen = transcriptManual ?? false;
  // Saved markup (read-only overlay, Milestone A). Empty/failed = no overlay,
  // never a broken reader.
  const markup = useMarkup(docId);
  const markupConfig = useMarkupConfig();
  // The active tool, so the undo/redo keyboard shortcuts only fire in annotation
  // edit mode (they must not shadow the browser / markdown editor otherwise).
  //
  // Select the BOOLEAN, not the tool. This subtree is 1,365 lines, and the only
  // question it asks is whether a tool is live at all. Subscribing to `tool`
  // re-rendered all of it on a pen -> highlighter switch, where the answer did not
  // change. zustand compares a selector's result with Object.is, so a boolean that
  // stays true costs nothing.
  const toolActive = useAnnotationTool((s) => s.tool !== "none");

  // ── The Excalidraw engine's state (the classic engine's is above). Which
  // engine renders this document is resolved below (`engine`): content wins
  // over the user's preference, so nothing ever reopens in the wrong editor.
  const drawing = useDrawing(docId);
  const drawingConfig = useDrawingConfig();
  const enginePref = useWritingEngine((s) => s.engine);
  // PDFs on the Excalidraw engine read in pdfjs; this toggle swaps in the
  // drawing surface (the classic engine annotates inline instead).
  const [annotating, setAnnotating] = useState(false);
  const [hasDrawnLive, setHasDrawnLive] = useState<boolean | null>(null);
  const surfaceRef = useRef<DrawingSurfaceHandle | null>(null);
  useEffect(() => {
    setAnnotating(false);
    setHasDrawnLive(null);
  }, [docId]);

  // Fullscreen ("borderless paper"): the document becomes the whole surface —
  // title bar and action island collapse to a single exit control. Excalidraw's
  // own UI stays complete (zen mode would strip the tool-properties panel).
  const fullscreen = useShell((s) => s.fullscreen);
  const toggleFullscreen = useShell((s) => s.toggleFullscreen);

  // Throttle the "held offline" reassurance so a flurry of failing strokes
  // raises one toast, not dozens.
  const troubleAt = useRef(0);

  // The editing session's authoritative ink (north-star C). ReaderTab owns ONE
  // InkModel per document — it outlives every disposable PageEditor, so the undo
  // stack and the batched park-and-replay persistence survive an editor teardown
  // (a zoom rebuild, a page leaving the window) instead of dying with it. Disposed
  // (flushing any pending write) when the document changes or the tab unmounts.
  const model = useMemo(() => {
    const sendBatch = (req: MarkupBatchRequest) =>
      withRetry(() =>
        api.request<MarkupBatchResult>("POST", `/api/documents/${docId}/markup/batch`, req),
      ).then((res) => {
        // Keep the "Annotated PDF (with your ink)" download option (gated on
        // `markup.data` being non-empty) appearing IN-SESSION — WITHOUT the old
        // per-flush `invalidateQueries`, which refetched + reconciled + redrew
        // every stroke on every batch (O(total) work per stroke → loads that
        // crawled after a long session — Bug B). The InkModel already owns the
        // in-session ink for rendering; this cache only drives that one boolean,
        // so we flip it ONCE, cheaply, when the doc first gains ink, and leave
        // the cache's array identity stable thereafter (no redraw churn).
        if (res.created.length) {
          const cur = queryClient.getQueryData<Annotation[]>(keys.markup(docId));
          if (cur && cur.length === 0) {
            queryClient.setQueryData<Annotation[]>(
              keys.markup(docId),
              res.created.map((c) => c.annotation),
            );
          }
        }
        return res;
      });
    return new InkModel({
      docId,
      sendBatch,
      // The unload-safe drain (pagehide beacon) POSTs here directly; same-origin, so
      // the id-token cookie authenticates it without a header (see flushBeacon).
      beaconUrl: `/api/documents/${docId}/markup/batch`,
      // Only park + replay a TRANSIENT failure. A definitive 4xx on a MULTI-op
      // batch is re-queued and BISECTED (the chunk size halves); only a lone
      // rejected op is dropped by the model — so one bad stroke can't wedge or
      // discard the batch around it — and surfaced below.
      isTransient,
      onPersistTrouble: () => {
        // Throttle the reassurance so a flurry of failing writes raises one toast.
        const now = Date.now();
        if (now - troubleAt.current < 10_000) return;
        troubleAt.current = now;
        toast("Your markup is saved here and will sync once you're back online.");
      },
      onPersistReject: (e) => {
        toast.error("Couldn't save some markup", { description: rejectionReason(e) });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);
  // Own the model's lifecycle from an effect (NOT the useMemo body): attach its
  // window listeners on mount and dispose on unmount. This keeps construction pure
  // — a discarded StrictMode/dev double-render instance never leaks listeners — and
  // lets a StrictMode remount revive the model instead of leaving it disposed.
  useEffect(() => {
    model.attach();
    return () => model.dispose();
  }, [model]);

  // Persistence handlers for the Fabric editor: thin adapters onto the model. The
  // editor tags its object with the temp id `onCreate` returns synchronously;
  // batching, id resolution, and offline replay are all the model's job now.
  const persist = useMemo<PersistHandlers>(
    () => ({
      onCreate: (draft) => model.create(draft),
      onUpdate: (id, patch) => model.update(id, patch),
      onDelete: (id) => model.delete(id),
      // An ordered op set (an eraser drag, an area-split) commits as ONE model
      // transaction → one undo entry → one batch write (plan F2).
      onTransact: (ops) => model.transact(ops),
      // The lasso circled a region — queue a figure-explain of the scanned
      // image (fires straight to /api/ai/explain; see the effect below).
      onRegion: (image, _rect, page) => setRegionScan({ image, page }),
      // The bucket found nothing to fill. Say which of the three reasons it was
      // — a tap that silently does nothing reads as a broken tool.
      onFillRefused: (reason) => toast(FILL_REFUSALS[reason]),
    }),
    [model],
  );

  // Leave editing mode when the document CHANGES — switching documents should
  // not carry a live pen onto the next one. It deliberately does NOT reset on
  // unmount any more: that fired on refresh too, so a reload mid-session dropped
  // you back to read mode even though the tool is persisted. `docId` in the
  // cleanup closure identifies which document we are leaving.
  const lastDocId = useRef(docId);
  useEffect(() => {
    if (lastDocId.current !== docId) {
      lastDocId.current = docId;
      useAnnotationTool.getState().reset();
    }
  }, [docId]);

  // Blank-canvas notes render an infinite annotatable surface, not a document.
  const isCanvas = doc?.source_type === "canvas";
  const isPdf = useMemo(
    () =>
      !!doc &&
      !isCanvas &&
      (doc.has_pdf_snapshot ||
        doc.mime === "application/pdf" ||
        doc.source_ref.toLowerCase().endsWith(".pdf")),
    [doc, isCanvas],
  );
  // Which writing engine renders this document. Content wins over the user's
  // preference: a canvas reopens in the engine that owns it (canvas_engine,
  // pre-toggle notes ⇒ legacy), a PDF follows whichever store holds its ink,
  // and only an untouched surface follows the preference.
  const engine = resolveEngine({
    preference: enginePref,
    canvasEngine: doc?.canvas_engine,
    isCanvas: !!isCanvas,
    hasLegacyMarkup: Array.isArray(markup.data) ? markup.data.length > 0 : undefined,
    hasSceneContent: drawing.data
      ? (drawing.data.scene?.elements?.some((e) => !e.locked) ?? false)
      : undefined,
  });
  // Warm the code-split writing-engine chunk as soon as a document opens (idle,
  // off the first paint) so the session's FIRST pen contact doesn't wait on a
  // cold dynamic import — the engine stays out of the initial bundle, it just
  // starts downloading when a reader tab mounts instead of at first edit-mode
  // entry (F1.3). The pen-down buffer (CanvasView/PdfViewer) bridges whatever gap
  // remains until the editors attach.
  //
  // Warm ONLY the engine this document resolves to. Warming both spent 4,783,069
  // bytes on the Excalidraw chunk at every reader open, including for the Classic
  // notes that are the default and the product. See atlas WE-35 and WE-36.
  useEffect(() => {
    const ric = window as unknown as {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (h: number) => void;
    };
    let idle = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Swallow a warm-up failure: this is a best-effort prefetch, and the real
    // edit-mode entry (with its own toast + retry) owns surfacing a load error.
    const warm = () => {
      if (engine === "excalidraw") void loadExcalidraw().catch(() => {});
      else void loadFabric().catch(() => {});
    };
    if (ric.requestIdleCallback) idle = ric.requestIdleCallback(warm);
    else timer = setTimeout(warm, 0);
    return () => {
      if (idle) ric.cancelIdleCallback?.(idle);
      if (timer) clearTimeout(timer);
    };
  }, [engine]);

  // Code/data files read best in a monospace column (no reflow).
  const isCode = useMemo(
    () => !!doc && /\.(py|pyi|js|jsx|mjs|cjs|ts|tsx|java|kt|kts|scala|c|h|cpp|cc|cxx|hpp|cs|go|rs|swift|rb|php|pl|lua|r|sh|bash|zsh|ps1|sql|dart|ex|exs|hs|clj|vue|svelte|json|jsonc|yaml|yml|toml|ini|cfg|env|xml|csv|tsv|tex|gradle)$/i.test(doc.source_ref),
    [doc],
  );
  const fileUrl = doc?.has_pdf_snapshot
    ? `/api/documents/${docId}/printed-pdf`
    : `/api/documents/${docId}/file`;

  // Can this document be written on with the CLASSIC engine — i.e. does the pen
  // belong on the control islands at all? Excalidraw-engine documents are
  // excluded: their entry point is `annotating` (which swaps the whole surface),
  // and the classic tool this pen sets would render nothing on them.
  const canInk =
    engine === "legacy" && (isCanvas || isPdf) && (markupConfig.data?.enabled ?? false);
  // Ctrl+Z / Ctrl+Y (Ctrl+Shift+Z) drive the InkModel's undo stack — but ONLY on a
  // pdf/canvas document in annotation edit mode, and never while typing into a
  // Fabric text box (its hidden textarea) or any input, so the shortcut never
  // shadows the browser's own undo or the markdown editor's. Gated on `visible` so
  // only the foreground reader tab responds.
  const editing = canInk && toolActive;
  useEffect(() => {
    if (!visible || !editing) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      const undo = k === "z" && !e.shiftKey;
      const redo = k === "y" || (k === "z" && e.shiftKey);
      if (!undo && !redo) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      e.preventDefault();
      if (undo) model.undo();
      else model.redo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, editing, model]);

  // Explain a selection / a lasso-scanned figure. This is the ONE remaining
  // "ask the AI about this" affordance inside the reader — it fires straight
  // to /api/ai/explain (save: true persists the answer as an annotation node,
  // exactly as before) rather than opening its own competing chat surface;
  // the result shows up in the AppShell RightBar's Annotations pane. A full
  // conversation about the document is reached via the single ChatLauncher/
  // ask panel (LeftBar → Tools → "Ask about this document").
  const { start: startExplain } = useAiStream();
  const [explaining, setExplaining] = useState(false);
  const explainSelection = async (mode: Mode, sel: Selection) => {
    if (explaining) return;
    setExplaining(true);
    setSelection(null);
    window.getSelection()?.removeAllRanges();
    try {
      const { meta } = await startExplain("/api/ai/explain", {
        mode,
        text: sel.text,
        document_id: docId,
        save: true,
        anchor: sel.page ? { page: sel.page } : undefined,
      });
      void queryClient.invalidateQueries({ queryKey: keys.usage });
      if (meta.savedNodeId) {
        void queryClient.invalidateQueries({ queryKey: keys.annotations(docId) });
        void queryClient.invalidateQueries({ queryKey: keys.overview });
        // The saved explanation is a new vault entry too — refresh the file
        // explorer at once rather than on its 30s poll (Part D).
        void queryClient.invalidateQueries({ queryKey: keys.vault });
        // Actually reveal the saved annotation: the RightBar is collapsed on
        // the "pins" pane by default, so a "see the right bar" toast otherwise
        // points at nothing. Open it on the Annotations pane.
        useShell.getState().openRightBar("annotations");
        toast.success("Explained — saved to Annotations", { description: "See the right bar." });
      } else if (meta.error) {
        toast.error("Could not explain", { description: meta.error });
      }
    } finally {
      setExplaining(false);
    }
  };

  // A lasso region scan: explain the circled image via a vision model (mode
  // "figure" sends image_b64). Fires once per scan, same save-as-annotation
  // path as a text selection.
  useEffect(() => {
    if (!regionScan) return;
    const scan = regionScan;
    setRegionScan(null);
    if (explaining) return;
    void (async () => {
      setExplaining(true);
      try {
        const { meta } = await startExplain("/api/ai/explain", {
          mode: "figure",
          image_b64: scan.image,
          document_id: docId,
          save: true,
          anchor: { page: scan.page },
          custom_prompt: "Explain the circled region of this page in plain language.",
        });
        void queryClient.invalidateQueries({ queryKey: keys.usage });
        if (meta.savedNodeId) {
          void queryClient.invalidateQueries({ queryKey: keys.annotations(docId) });
          void queryClient.invalidateQueries({ queryKey: keys.overview });
          void queryClient.invalidateQueries({ queryKey: keys.vault });
          // Same as explainSelection: open the RightBar's Annotations pane so
          // the saved region-explanation is actually visible.
          useShell.getState().openRightBar("annotations");
          toast.success("Explained the circled region — saved to Annotations", {
            description: "See the right bar.",
          });
        } else if (meta.error) {
          toast.error("Could not explain", { description: meta.error });
        }
      } finally {
        setExplaining(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionScan]);

  const printWebsite = async () => {
    setPrinting(true);
    try {
      const { job_id } = await api.request<{ job_id: string }>(
        "POST",
        `/api/documents/${docId}/print-pdf`,
      );
      useActivity.getState().upsert({
        id: job_id,
        kind: "print_web_pdf",
        title: `print PDF · ${doc?.title ?? docId}`,
        status: "queued",
      });
      const job = await trackJob(job_id, docId);
      if (job.status !== "done") {
        toast.error("Could not print website", { description: job.error ?? "" });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: keys.document(docId) });
      await queryClient.invalidateQueries({ queryKey: keys.reader(docId) });
      toast.success("PDF snapshot is ready for markup");
    } catch (error) {
      toast.error("Could not print website", { description: String(error) });
    } finally {
      setPrinting(false);
    }
  };

  // Download the document as a real file named after its TITLE (Part C). The old
  // path was `window.open(fileUrl)`, which is popup-blocker prone and — for a
  // canvas note (no meaningful original) — opened a dead tab. Pick the right
  // NATIVE artifact per type: a canvas renders a cropped PDF first (synchronous
  // Pillow render, served via /printed-pdf), a printed web doc saves its
  // snapshot, everything else saves its original bytes. Always a BLOB download.
  const downloadLabel = isCanvas
    ? "Download as PDF"
    : doc?.has_pdf_snapshot
      ? "Download PDF snapshot"
      : "Download original";
  const downloadOriginal = async () => {
    if (!doc) return;
    const base = sanitizeFilename(doc.title, docId);
    if (isCanvas) {
      // Render the note to a PDF now (fast, synchronous), then it's at
      // /printed-pdf. A classic-engine note renders server-side from its
      // stroke rows (no body); an Excalidraw note posts the surface's own
      // PNG snapshot (the server never runs Excalidraw).
      if (engine === "excalidraw") {
        const snapshot = await surfaceRef.current?.exportPng();
        if (!snapshot) {
          toast.error("This note is empty — draw or type something first.");
          return;
        }
        await api.request("POST", `/api/documents/${docId}/canvas-pdf`, { dataURL: snapshot });
      } else {
        await api.request("POST", `/api/documents/${docId}/canvas-pdf`);
      }
      // has_pdf_snapshot flips true — refresh the doc so the button/reader agree.
      await queryClient.invalidateQueries({ queryKey: keys.document(docId) });
      void queryClient.invalidateQueries({ queryKey: keys.reader(docId) });
      await downloadUrlAsFile(`/api/documents/${docId}/printed-pdf`, base, ".pdf");
    } else if (doc.has_pdf_snapshot) {
      await downloadUrlAsFile(`/api/documents/${docId}/printed-pdf`, base, ".pdf");
    } else {
      // Original bytes: prefer the mime / source-filename extension, and let the
      // response's own Content-Type decide when neither is conclusive.
      await downloadUrlAsFile(
        `/api/documents/${docId}/file`,
        base,
        extensionFor(doc.mime, doc.source_ref),
      );
    }
  };

  // Flatten this PDF's ink onto its pages server-side, then download it. POST is
  // synchronous (`created:false` = cached, unchanged since last render); the
  // response carries the servable file_url + the final filename.
  const downloadAnnotated = async () => {
    if (!doc) return;
    const res = await api.request<{ created: boolean; file_url: string; filename: string }>(
      "POST",
      `/api/documents/${docId}/annotated-pdf`,
    );
    await downloadArtifact(res, sanitizeFilename(doc.title, docId));
  };

  // Compile a markdown-sourced document's body to a styled PDF server-side, then
  // download it. Same POST-then-download shape as the annotated path.
  const downloadCompiledPdf = async () => {
    if (!doc) return;
    const res = await api.request<{ created: boolean; file_url: string; filename: string }>(
      "POST",
      `/api/documents/${docId}/compiled-pdf`,
    );
    await downloadArtifact(res, sanitizeFilename(doc.title, docId));
  };

  // One runner for every download item: mark it busy (its spinner + the trigger
  // spinner), run it, route any failure through notifyError, then close the menu
  // (kept open until now so the in-item spinner was visible). Re-entrancy guarded.
  const runDownload = async (key: string, fn: () => Promise<void>) => {
    if (!doc || busyDownload) return;
    setBusyDownload(key);
    try {
      await fn();
    } catch (e) {
      notifyError(e);
    } finally {
      setBusyDownload(null);
      setDownloadMenuOpen(false);
    }
  };

  // The download artifacts on offer for this document. A markdown-sourced doc can
  // save its raw .md OR a compiled PDF; a real PDF can save its original bytes,
  // plus a flattened "annotated" PDF once it carries ink. Web docs and canvases
  // keep their single native action (no menu) — see the render below. 2+ items ⇒
  // the Download button becomes a per-type menu.
  const isWebDoc = doc?.source_type === "web";
  const isMarkdownDoc =
    !!doc &&
    !isCanvas &&
    !isWebDoc &&
    ((doc.mime ?? "").startsWith("text/markdown") || /\.(md|markdown)$/i.test(doc.source_ref));
  // Either engine's ink gates the "Annotated PDF" download (locked Excalidraw
  // elements are surface furniture — page backdrops/paper — not user content).
  const hasMarkup =
    (markup.data?.length ?? 0) > 0 ||
    (hasDrawnLive ?? (drawing.data?.scene?.elements?.some((e) => !e.locked) ?? false));
  const downloadItems: { key: string; label: string; run: () => Promise<void> }[] = [];
  if (isMarkdownDoc) {
    downloadItems.push({ key: "markdown", label: "Markdown (.md)", run: downloadOriginal });
    downloadItems.push({ key: "compiled", label: "PDF", run: downloadCompiledPdf });
  } else if (isPdf && !isWebDoc) {
    downloadItems.push({ key: "original", label: "Original PDF", run: downloadOriginal });
    if (hasMarkup) {
      downloadItems.push({
        key: "annotated",
        label: "Annotated PDF (with your ink)",
        run: downloadAnnotated,
      });
    }
  }
  const useDownloadMenu = downloadItems.length >= 2;

  const exportDoc = async () => {
    const { job_id } = await api.createExport({ kind: "document_markdown", document_id: docId } as never);
    useActivity.getState().upsert({ id: job_id, kind: "export", title: `export · ${doc?.title ?? docId}`, status: "queued" });
    const job = await trackJob(job_id);
    if (job.status === "done") {
      toast.success("Export landed in your vault", {
        action: { label: "Download", onClick: () => window.open(`/api/exports/${job_id}/download`, "_blank") },
      });
    } else {
      toast.error("Export failed", { description: job.error ?? "" });
    }
  };

  if (reader.isLoading) {
    return (
      <div className={cn("space-y-3 p-6", !visible && "hidden")}>
        <Skeleton className="h-8 w-2/5" />
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-64" />
      </div>
    );
  }
  if (reader.isError || !reader.data) {
    // A 404 self-closes this tab (see the effect above) — render nothing
    // rather than flash a dead-end error the user can't act on.
    if (reader.error instanceof GraphMindApiError && reader.error.status === 404) {
      return <div className={cn("h-full", !visible && "hidden")} />;
    }
    return (
      <div className={cn("grid h-full place-items-center", !visible && "hidden")}>
        <ErrorState error={reader.error} onRetry={() => void reader.refetch()} label="document" />
      </div>
    );
  }

  const { sections } = reader.data;
  const processing = doc?.status === "processing";
  // A PDF is shown purely from its metadata (mime / .pdf / snapshot). We do NOT
  // gate it behind a separate availability probe: that probe was a raw fetch
  // authenticated only by the id-token cookie, which lands a beat after reopen /
  // is absent on a new device — so its 401 used to "downgrade" a real PDF to its
  // OCR text. The PdfViewer owns load failures (it retries, then shows an inline
  // error) and never silently falls back to the extracted text.
  const showPdf = isPdf;

  // True when the mounted Excalidraw surface shows its own top-right exit
  // control in fullscreen (only the editable surface renders that row).
  const surfaceOwnsExit =
    engine === "excalidraw" &&
    (drawingConfig.data?.enabled ?? false) &&
    !!drawing.data &&
    (isCanvas || (showPdf && annotating));

  // Annotatable docs (canvas + pdf) get a "Text" side-tab showing the handwriting
  // transcript the OCR sweep produces (plan §3.4). It rides the document query —
  // no new polling — so it refreshes whenever `doc` refetches.
  const showTranscript = isCanvas || isPdf;
  const transcript = doc?.annotation_ocr ?? null;
  const transcriptAt = doc?.annotation_ocr_at ?? null;

  return (
    <div className={cn("relative flex h-full min-h-0 flex-col", !visible && "hidden")}>
      {/* The one thin, borderless title bar (redesign point 2): the document
          name, centred + muted + editable — renames the vault file (and, for a
          canvas, is the note's only name). Collapsed in fullscreen — the sheet
          is borderless paper there. */}
      {!fullscreen && (
        <DocTitleBar
          title={doc?.title ?? "Untitled"}
          onRename={(next) => renameDoc.mutate(next)}
          busy={renameDoc.isPending}
          renameHint="Rename document"
        />
      )}

      {/* Fullscreen keeps a SMALL island over the paper: the pen, then the exit
          control — but ONLY while there is no markup toolbar. In write mode the
          toolbar carries Read/Write and Exit in its own trailing group, so one
          bar holds everything clickable instead of a bar plus a corner island
          that each had to be positioned around the other. Faint until touched or hovered so it never reads as chrome.
          The editable Excalidraw surface renders its own island instead, so this
          is the classic engine's copy.

          The pen HAS to be here. Fullscreen collapses the doc-controls island,
          which is where it otherwise lives, so entering fullscreen from read
          mode used to leave "Exit fullscreen" as the only control on the page
          and no way into the toolbar at all. On a tablet that is a dead end:
          there is no Esc key, and the read-mode auto-activate that opens the
          tools on a pen contact is stylus-only (see PenDownBuffer), so a finger
          had to leave fullscreen, tap the pen, and come back.

          It sits IN the top-right corner, level with the surface's own chrome
          row — the reachable place on a held tablet. It does NOT dodge the
          markup toolbar by sitting below it: that put it a third of the way
          down the page and made it awkward to reach. The toolbar dodges IT
          instead, by reserving this corner while fullscreen (see the `anchor`
          in AnnotationToolbar); the toolbar is centred and scrolls internally,
          so giving up the corner costs it nothing. There is an e2e test that
          the two boxes stay disjoint at phone and tablet widths. */}
      {fullscreen && !surfaceOwnsExit && !editing && (
        <div className="pointer-events-none absolute right-2 top-1 z-30 flex justify-end">
          <div className="pointer-events-auto flex items-center gap-0.5 rounded-lg border border-border bg-popover/70 px-1 py-1 opacity-60 shadow-md backdrop-blur transition-opacity hover:opacity-100 focus-within:opacity-100">
            {canInk && <ModeSwitch on={editing} />}
            {showPdf && engine === "excalidraw" && (drawingConfig.data?.enabled ?? false) && (
              <ExcalidrawPdfPen
                on={annotating}
                ready={!!drawing.data}
                onToggle={() => setAnnotating((v) => !v)}
              />
            )}
            <Tip label="Exit fullscreen — Esc">
              <Button
                variant="ghost"
                size="iconLg"
                aria-label="Exit fullscreen"
                onClick={toggleFullscreen}
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
            </Tip>
          </div>
        </div>
      )}

      {/* Doc actions — ONE floating top-right island (redesign point 3) hovering
          over the document, consuming no layout width, modelled on the
          annotation toolbar: annotate, lock, panel, download, fullscreen, and
          the less-frequent exports folded into the ⋮ overflow. The pen lives
          here rather than in its own floating pill: three separately-positioned
          controls on this edge kept colliding with each other, and stacking them
          in reserved rows put the pen uncomfortably far down the page.

          The surfaces below leave this corner free for it — see the chrome rows
          in CanvasView / InfiniteCanvasView / PdfViewer, whose zoom and page
          controls sit beside their label rather than at the right edge.

          Vertically it straddles the title row and the surface's chrome row —
          it is 46px tall and both rows are 36px, so it cannot sit wholly inside
          either. `top-6` is the window where it covers neither the chrome row's
          controls (they start from the left) nor the top edge of the markup
          toolbar below (`top-9` clipped it by a few px at tablet-portrait and
          phone widths). It CANNOT go higher: `.wp-panel-fill` clips at the
          panel's top edge, so a negative offset doesn't render it over the
          header — it erases it. In fullscreen it is replaced by the small
          island above — the paper is borderless there, so only the pen and the
          exit survive. */}
      <div className={cn("pointer-events-none absolute right-2 top-[37px] z-30", fullscreen && "hidden")}>
        <div className="pointer-events-auto flex items-center gap-0.5 rounded-lg border border-border bg-popover px-1 py-0.5 shadow-deep">
          {canInk && <ModeSwitch on={editing} />}
          <Tip
            label={
              isPublic
                ? "Public — anyone with your atlas can see this. Click to make private"
                : "Private. Click to publish this document to your atlas"
            }
          >
            <Button
              variant="ghost"
              size="icon"
              aria-pressed={isPublic}
              aria-label={isPublic ? "Make private" : "Make public"}
              disabled={!doc || setVisibility.isPending}
              onClick={() =>
                setVisibility.mutate(isPublic ? "private" : "public", {
                  onSuccess: (res) =>
                    toast.success(
                      res.visibility === "public" ? "Published to your atlas" : "Made private",
                    ),
                  onError: (e) =>
                    toast.error("Could not change visibility", { description: String(e) }),
                })
              }
            >
              {setVisibility.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isPublic ? (
                <Globe className="h-4 w-4 text-primary" />
              ) : (
                <Lock className="h-4 w-4" />
              )}
            </Button>
          </Tip>
          {showPdf && engine === "excalidraw" && (drawingConfig.data?.enabled ?? false) && (
            <ExcalidrawPdfPen
              on={annotating}
              ready={!!drawing.data}
              onToggle={() => setAnnotating((v) => !v)}
            />
          )}
          {showTranscript && (
            <Tip label={transcriptOpen ? "Hide handwriting transcript" : "Show handwriting transcript"}>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setTranscriptManual(!transcriptOpen)}
                aria-pressed={transcriptOpen}
                aria-label="Toggle handwriting transcript"
              >
                {transcriptOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
              </Button>
            </Tip>
          )}
          {doc?.source_type === "web" && (
            <Tip label="Open original website">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Open original website"
                onClick={() => window.open(doc.source_ref, "_blank", "noopener,noreferrer")}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </Tip>
          )}
          {doc?.source_type === "web" && (
            <Tip label={doc.has_pdf_snapshot ? "Open PDF snapshot" : "Print website to PDF"}>
              <Button
                variant="ghost"
                size="icon"
                aria-label={doc.has_pdf_snapshot ? "Open PDF snapshot" : "Print website to PDF"}
                disabled={printing}
                onClick={() =>
                  doc.has_pdf_snapshot
                    ? window.open(`/api/documents/${docId}/printed-pdf`, "_blank")
                    : void printWebsite()
                }
              >
                {printing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Printer className="h-4 w-4" />
                )}
              </Button>
            </Tip>
          )}
          {useDownloadMenu ? (
            <Dropdown open={downloadMenuOpen} onOpenChange={(o) => setDownloadMenuOpen(o)}>
              <Tip label="Download">
                <DropdownTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Download" disabled={!doc || downloading}>
                    {downloading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                </DropdownTrigger>
              </Tip>
              <DropdownContent align="end">
                {downloadItems.map((item) => (
                  <DropdownItem
                    key={item.key}
                    closeOnClick={false}
                    disabled={downloading}
                    onSelect={() => void runDownload(item.key, item.run)}
                  >
                    {busyDownload === item.key ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                    ) : (
                      <Download className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    {item.label}
                  </DropdownItem>
                ))}
              </DropdownContent>
            </Dropdown>
          ) : (
            <Tip label={downloadLabel}>
              <Button
                variant="ghost"
                size="icon"
                aria-label={downloadLabel}
                onClick={() => void runDownload("original", downloadOriginal)}
                disabled={!doc || downloading}
              >
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              </Button>
            </Tip>
          )}
          <Tip label="Fullscreen — the document as a borderless sheet (Esc exits)">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Enter fullscreen"
              onClick={toggleFullscreen}
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          </Tip>
          {/* Overflow ⋮ — the less-frequent exports, kept off the primary row. */}
          <Dropdown>
            <Tip label="More document actions">
              <DropdownTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="More document actions">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownTrigger>
            </Tip>
            <DropdownContent align="end">
              {/* Selecting this CLOSES the menu and hands over to the panel —
                  leaving the dropdown open on top of it just puts two surfaces
                  in the same corner, the second one unreachable. */}
              {isCanvas && (
                <DropdownItem onSelect={() => setPaperOpen(true)}>
                  <Palette className="h-3.5 w-3.5 text-muted-foreground" />
                  Paper — background, lines, spacing
                </DropdownItem>
              )}
              <DropdownItem onSelect={() => void exportDoc()}>
                <FileOutput className="h-3.5 w-3.5 text-muted-foreground" />
                Export markdown to vault
              </DropdownItem>
              <DropdownItem disabled={!doc} onSelect={() => setTieredOpen(true)}>
                <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                Smart tiered export (agent-ready ZIP)
              </DropdownItem>
            </DropdownContent>
          </Dropdown>
        </div>
      </div>

      {/* The paper control. A note's surface reads its paper from the document
          query, so the control writes there first and the canvas repaints on the
          next frame — the PATCH follows behind. */}
      {paperOpen && isCanvas && doc && (
        <div className="pointer-events-none absolute inset-0 z-40">
          <div className="pointer-events-auto absolute right-2 top-16 rounded-lg border border-border bg-popover p-3 shadow-deep">
            <PaperControl doc={doc} />
            <button
              type="button"
              onClick={() => setPaperOpen(false)}
              aria-label="Close paper settings"
              className="mt-3 w-full rounded border border-border py-1 text-[11px] text-muted-foreground hover:text-foreground"
            >
              Done
            </button>
          </div>
        </div>
      )}

      <TieredExportDialog
        open={tieredOpen}
        onOpenChange={setTieredOpen}
        docId={docId}
        title={doc?.title ?? docId}
      />

      <div className="relative flex min-h-0 flex-1">
        {/* The original document (selectable) — full width unless the
            handwriting transcript is open alongside it. */}
        <div
          className={cn(
            "relative min-w-0",
            compact || !transcriptOpen ? "w-full flex-1" : "w-[64%] shrink-0 border-r border-border",
          )}
        >
          {isCanvas && engine === "excalidraw" ? (
            drawing.data ? (
              <ExcalidrawSurface
                ref={surfaceRef}
                docId={docId}
                initial={drawing.data}
                editable={drawingConfig.data?.enabled ?? false}
                background={doc?.canvas_background}
                onRegion={(image, page) => setRegionScan({ image, page })}
                onContentChange={setHasDrawnLive}
              />
            ) : (
              <div className="grid h-full place-items-center">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            )
          ) : isCanvas ? (
            <CanvasView
              docId={docId}
              background={doc?.canvas_background ?? "#ffffff"}
              persist={persist}
              model={model}
              initialPages={doc?.page_count ?? 1}
              layout={doc?.canvas_layout === "infinite" ? "infinite" : "pages"}
              paper={doc?.canvas_paper}
              paperSpacing={doc?.canvas_paper_spacing ?? undefined}
              title={doc?.title}
              onRename={(next) => renameDoc.mutate(next)}
              createdAt={doc?.created_at}
            />
          ) : showPdf && engine === "excalidraw" && annotating && drawing.data ? (
            <ExcalidrawSurface
              ref={surfaceRef}
              docId={docId}
              initial={drawing.data}
              editable={drawingConfig.data?.enabled ?? false}
              pdfUrl={fileUrl}
              onRegion={(image, page) => setRegionScan({ image, page })}
              onContentChange={setHasDrawnLive}
            />
          ) : showPdf ? (
            <PdfViewer
              docId={docId}
              url={fileUrl}
              annotations={markup.data ?? []}
              annotationsLoading={markup.isLoading}
              editable={engine === "legacy" && (markupConfig.data?.enabled ?? false)}
              fonts={markupConfig.data?.fonts ?? []}
              persist={persist}
              model={model}
              chromeless={fullscreen}
              onSelect={(s: PdfSelection | null) => setSelection(s ? { text: s.text, page: s.page } : null)}
            />
          ) : (
            <TextSource
              sections={sections}
              processing={!!processing}
              onSelect={setSelection}
              code={isCode}
              markdown={isMarkdownDoc}
            />
          )}

          {/* Explain-selection mini action bar — the one place the reader still
              reaches into the AI: it saves straight to Annotations (right bar)
              rather than opening a competing chat surface. */}
          {selection && (
            <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center px-4">
              <div className="pointer-events-auto flex items-center gap-0.5 rounded-full border border-primary/30 bg-card/95 py-1 pl-2.5 pr-1.5 shadow-deep backdrop-blur animate-rise-in">
                <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
                {MODES.map((m) => (
                  <Tip key={m.mode} label={m.hint}>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={explaining}
                      onClick={() => void explainSelection(m.mode, selection)}
                    >
                      {m.label}
                    </Button>
                  </Tip>
                ))}
                {explaining && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin text-primary" />}
              </div>
            </div>
          )}
        </div>

        {/* Handwriting transcript — the one remaining reader-local side pane.
            Desktop: a docked right column. Compact: a draggable popup. */}
        {transcriptOpen &&
          showTranscript &&
          (compact ? (
            <DraggablePanel
              title="Transcript"
              onClose={() => setTranscriptManual(false)}
              width={Math.min(380, typeof window !== "undefined" ? window.innerWidth - 32 : 380)}
            >
              <div className="flex h-[min(60vh,520px)] min-h-0 flex-col">
                <TranscriptView transcript={transcript} at={transcriptAt} />
              </div>
            </DraggablePanel>
          ) : (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <div className="hairline-b flex min-h-[44px] shrink-0 items-center gap-2 px-3 py-1.5">
                <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <FileText className="h-3 w-3" /> Transcript
                </span>
              </div>
              <TranscriptView transcript={transcript} at={transcriptAt} />
            </div>
          ))}
      </div>
    </div>
  );
}

/** The classic engine's pen — the way into, and back out of, markup edit mode.
 *
 * It renders in TWO islands (the doc controls, and the small fullscreen one), so
 * it is one component rather than two copies free to drift apart.
 *
 * `on` is the live ANNOTATION TOOL. It used to read the Excalidraw surface's
 * `annotating` flag instead — a different engine's state, permanently false on a
 * classic document — so the pen never showed as pressed and every tap re-entered
 * the tool it was already in: a one-way switch. Its label is deliberately not the
 * toolbar's own "Done annotating" ✕, so while writing the two remain separately
 * addressable (by a screen reader, and by the e2e specs). */
/** The Excalidraw engine's equivalent on a PDF: it swaps the pdf.js reader for
 * the drawing surface rather than setting a tool. Same two islands, same reason
 * (see {@link AnnotatePen}) — fullscreen must not be a one-way door into reading. */
function ExcalidrawPdfPen({
  on,
  ready,
  onToggle,
}: {
  on: boolean;
  /** The scene has loaded; until then there is nothing to draw onto. */
  ready: boolean;
  onToggle: () => void;
}) {
  return (
    <Tip label={on ? "Back to reading (selectable text)" : "Annotate — draw and write on this PDF"}>
      <Button
        variant="ghost"
        size="iconLg"
        aria-pressed={on}
        aria-label={on ? "Stop annotating" : "Annotate this PDF"}
        disabled={!ready}
        onClick={onToggle}
      >
        <PenLine className={cn("h-4 w-4", on && "text-primary")} />
      </Button>
    </Tip>
  );
}

function TextSource({
  sections,
  processing,
  onSelect,
  code = false,
  markdown = false,
}: {
  sections: { id: string; label: string; text: string; anchor: Record<string, unknown> | null }[];
  processing: boolean;
  onSelect: (s: Selection | null) => void;
  code?: boolean;
  /** The document was ingested from markdown (`.md`/`.markdown` or a
   * `text/markdown` mime): render each section's body FORMATTED via <Markdown>
   * rather than as raw source, so `- bullet` / `[link](url)` read as prose.
   * The explain-selection flow is unaffected — it reads
   * `window.getSelection().toString()` off the rendered DOM (see `onMouseUp`),
   * which yields the visible text whether the surface is raw or rendered. */
  markdown?: boolean;
}) {
  const onMouseUp = () => {
    const sel = window.getSelection();
    const text = sel?.toString().trim() ?? "";
    onSelect(text.length >= 4 ? { text } : null);
  };
  return (
    <div className="h-full overflow-y-auto py-6" onMouseUp={onMouseUp}>
      {/* Prose reads in the same centered `.md-column` as the markdown editor,
          so reading and writing share one measure/gutters. Code keeps a
          full-bleed monospace column (no reflow) with its own gutters. */}
      <div className={cn("space-y-7 pb-24", code ? "mx-auto max-w-none px-7" : "md-column")}>
        {sections.length === 0 && (
          <p className="font-display italic text-muted-foreground">
            {processing ? "Reading the document…" : "No readable text was extracted."}
          </p>
        )}
        {sections.map((s) => (
          <section key={s.id}>
            <h3 className="mb-1.5 font-display text-[15px] font-medium text-foreground">{s.label}</h3>
            {code ? (
              <pre className="overflow-x-auto rounded-md border border-border bg-card/50 p-3 font-mono text-[12.5px] leading-[1.6] text-foreground/90">
                {s.text}
              </pre>
            ) : markdown ? (
              <Markdown text={s.text} />
            ) : (
              <p className="whitespace-pre-wrap text-[13.5px] leading-[1.75] text-foreground/90">{s.text}</p>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

/** The handwriting transcript (plan §3.4): copyable + searchable, with an honest
 * empty state until the OCR sweep has read this document's ink. */
function TranscriptView({ transcript, at }: { transcript: string | null; at: string | null }) {
  const [filter, setFilter] = useState("");
  const [copied, setCopied] = useState(false);
  const text = (transcript ?? "").trim();
  const lines = useMemo(() => text.split(/\r?\n/), [text]);
  const q = filter.trim();
  const ql = q.toLowerCase();
  const shown = ql ? lines.filter((l) => l.toLowerCase().includes(ql)) : lines;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy to the clipboard");
    }
  };

  if (!text) {
    return (
      <div className="grid min-h-0 flex-1 place-items-center p-6">
        <p className="max-w-[34ch] text-center font-display text-sm italic leading-relaxed text-muted-foreground">
          No handwriting transcribed yet — the transcript refreshes periodically after you write.
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="hairline-b flex shrink-0 items-center gap-2 px-3 py-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter lines…"
            aria-label="Filter transcript"
            className="bg-card pl-7"
          />
        </div>
        <Tip label="Copy transcript">
          <Button variant="outline" size="icon" onClick={() => void copy()} aria-label="Copy transcript">
            {copied ? <Check className="h-3.5 w-3.5 text-viridian" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
        </Tip>
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-1 p-4">
          {shown.length === 0 ? (
            <p className="py-6 text-center font-display text-sm italic text-muted-foreground">
              No lines match “{q}”.
            </p>
          ) : (
            shown.map((line, i) => (
              <p
                key={i}
                className="whitespace-pre-wrap break-words font-mono text-[12.5px] leading-relaxed text-foreground/90"
              >
                {ql ? highlightMatches(line, q) : line || " "}
              </p>
            ))
          )}
        </div>
      </ScrollArea>
      {at && (
        <p className="shrink-0 border-t border-border px-3 py-1.5 font-mono text-[10px] text-muted-foreground">
          Transcript updated {timeAgo(at)}
        </p>
      )}
    </div>
  );
}

/** Wrap every case-insensitive occurrence of `q` in `line` with a highlight mark. */
function highlightMatches(line: string, q: string): ReactNode {
  const lower = line.toLowerCase();
  const ql = q.toLowerCase();
  const out: ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < line.length) {
    const idx = lower.indexOf(ql, i);
    if (idx === -1) {
      out.push(line.slice(i));
      break;
    }
    if (idx > i) out.push(line.slice(i, idx));
    out.push(
      <mark key={k++} className="rounded-sm bg-primary/30 text-foreground">
        {line.slice(idx, idx + q.length)}
      </mark>,
    );
    i = idx + q.length;
  }
  return out;
}

